import React, { useMemo } from 'react'
import { useApiQuery } from '../../../utils/api'
import { Loading, Error } from '../../Elements/Indicators'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../Elements/PageHeader'
import { SimpleStats } from '../../Elements/Stats'
import { UserProgressCard } from '../../Elements/Cards'
import { Header3, PSmallFaded } from '../../Elements/Type'
import {
  CosmosAnalysisData,
  CosmosAnalysisUserStats,
  JiraUser
} from '@jirax/common'

// Represents the basic sprint details
interface Sprint {
  id: number
  name:string
  state: string
  goal?: string
  startDate: string
  endDate: string
  users: (JiraUser & { role: string })[]
  analysisAvailable?: boolean
}

// Represents the merged user data for UI rendering
interface SprintUser extends CosmosAnalysisUserStats {
  avatar: string
  role: string
}

interface RouteParams extends Record<string, string | undefined> {
  projKey: string
  sprintId: string
}

interface UsersProps {
  users: SprintUser[]
}

const Users: React.FC<UsersProps> = ({ users }) => {
  return (
    <ul
      role='list'
      className='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
    >
      {users.map(user => (
        <UserProgressCard user={user} key={user.id} />
      ))}
    </ul>
  )
}

const SprintDetails: React.FC = () => {
  const { projKey, sprintId } = useParams<RouteParams>()
  const hasParams = Boolean(projKey && sprintId)

  // --- API Calls ---
  // 1. Fetch basic sprint details (name, dates, users)
  const {
    isLoading: sprintLoading,
    error: sprintError,
    data: sprint
  } = useApiQuery<Sprint>(['sprints', sprintId], {
    enabled: hasParams
  })

  // 2. Fetch the analysis data for the sprint
  const {
    isLoading: analysisLoading,
    error: analysisError,
    data: analysis
  } = useApiQuery<CosmosAnalysisData>(['analysis', 'sprints', sprintId], {
    enabled: hasParams && !!sprint // Fetch only after sprint data is available
  })

  // --- Data Merging ---
  // useMemo to combine sprint user info (avatar, role) with analysis stats
  const mergedUsers: SprintUser[] = useMemo(() => {
    if (!sprint?.users || !analysis?.byUser) {
      return []
    }

    const analysisUserMap = new Map(
      analysis.byUser.map(user => [user.id, user])
    )

    return sprint.users
      .map(sprintUser => {
        const userAnalysis = analysisUserMap.get(sprintUser.accountId)
        if (!userAnalysis) {
          return null // User might be in the project but have no activity in this sprint
        }
        return {
          ...userAnalysis,
          avatar: sprintUser.avatarUrl,
          role: sprintUser.role || 'Team Member'
        }
      })
      .filter((u): u is SprintUser => u !== null)
  }, [sprint, analysis])

  // --- Loading and Error Handling ---
  if (!hasParams) {
    return (
      <Error
        title={'Error Loading Sprint'}
        message={'Missing required parameters'}
      />
    )
  }

  if (sprintLoading || (hasParams && analysisLoading && !analysis)) {
    return <Loading message={`Loading sprint ${sprintId}...`} />
  }

  if (sprintError) {
    return (
      <Error
        title={'Error Loading Sprint'}
        message={(sprintError as Error).message}
      />
    )
  }

  if (!sprint) {
    return (
      <Error title={'Error Loading Sprint'} message={'Sprint data not found'} />
    )
  }

  // --- UI Configuration and Logic ---
  const headerConfig = {
    pageTitle: sprint.name,
    backLink: {
      label: 'Back',
      to: '/projects'
    },
    breadcrumb: [
      {
        label: 'Projects',
        to: '/projects'
      },
      {
        label: 'Project Details',
        to: `/projects/${projKey}`
      },
      {
        label: `Sprint ${sprintId}`
      }
    ],
    rightComponent: (
      <>
        {sprint.state !== 'active' && (
          <PSmallFaded
            text={`${new Date(sprint.startDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })} to ${new Date(sprint.endDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })}`}
          />
        )}

        {sprint.state === 'active' && (
          <PSmallFaded
            text={`Active, started on ${new Date(
              sprint.startDate
            ).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })}`}
          />
        )}
      </>
    )
  }

  const getStats = () => {
    if (!analysis) return []

    const delivered =
      analysis.startPoints > 0
        ? analysis.resolvedPoints / analysis.startPoints
        : 0

    return [
      {
        name: 'Points',
        value: `${analysis.resolvedPoints} / ${analysis.startPoints}`
      },
      {
        name: 'Bugs',
        value: `${analysis.resolvedBugs} / ${analysis.startBugs}`
      },
      {
        name: 'Commitment Delivered',
        value: Number(delivered).toLocaleString(undefined, {
          style: 'percent',
          minimumFractionDigits: 2
        })
      }
    ]
  }

  return (
    <>
      <PageHeader config={headerConfig} />
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {analysisError ? (
          <div className="mt-4">
            <Error
              title={'Analysis Not Available'}
              message={'Sprint analysis data could not be loaded for this sprint.'}
            />
          </div>
        ) : (
          <>
            <SimpleStats title={'Summary'} stats={getStats()} />
            <div className='mt-4'>
              <Header3 title={'By User'} />
              <Users users={mergedUsers} />
            </div>
          </>
        )}
      </main>
    </>
  )
}

export default SprintDetails
